import { useTranslation } from "react-i18next";
import Table from "../modules/merchandising/components/tables/BulkActionsTable";
import BreadcrumbDates from "../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../components/custom/merchandise/MetricsCard";
import { DatesProvider, useDates } from "../dates/DatesContext";
import { useDateFormatter } from "../i18n/useDateFormatter";
import { useCategoriesQuery } from "../hooks/useCategories";
import useQueryParams from "../hooks/useQueryParams";
import { useMemo, useRef, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useAppSelector } from "../redux/hooks";
import getMetricSpec from "../modules/merchandising/utils/metrics";
import { formatDatamartFilters } from "../utils/formatV2DatamartFilters";
import { useFetch } from "../hooks/api";
import { getURLPrefix } from "../utils/getUrlPrefix";
import NoImage from "../assets/images/utility/no_image.png";
import TrellisLink from "../components/core/basic/TrellisLink";
import { ProductCell } from "../modules/merchandising/components/cells/ProductCell";
import useCustomTableFilters from "../hooks/useCustomTableFilters";
import useProducts from "../hooks/useProducts";
import useHaloRank from "../hooks/useHaloRank";
import BarChart from "../components/core/blocks/BarChart";
import { metricColors } from "../components/custom/analytics/palettes";
import ProductShopperInsights from "../components/custom/merchandise/ProductShopperInsights";
import ProductCardHeader from "../components/custom/merchandise/ProductCardHeader";
import { formatCurrency, formatNumber } from "../utils/formatNumber";
import { Accordion } from "../components/core/basic/Accordion";
import { csvParse } from "d3-dsv";
import { useModuleEnabled } from "../redux/selectors/modules";
import { DASHBOARD_LINKS } from "../utils/dashboardLinks";
import { AMC_QUERY_EXPLORER_BREADCRUMB_SPEC } from "../modules/amc/utils/utils";
import { castNumbersInRowData } from "../modules/amc/utils/pathToPurchase/utils";
import { ButtonGroup } from "../components/core/basic/ButtonGroup";

const URL_PREFIX = getURLPrefix();

const useLatestAMCData = (report) => {
  const hasAMCModule = useModuleEnabled("AMC");
  const { data: reports, isLoading } = useFetch(
    ["amc_reports", report],
    "/amc/reports/",
    {
      ordering: "-created_on",
      query_type: report,
      // SUCCESS
      export__status: 2,
      limit: 1,
    },
    {
      enabled: hasAMCModule,
      select: (res) => res.data?.results,
      default: [],
    },
  );

  const exportReport = useMemo(() => {
    return reports?.[0]?.export;
  }, [reports]);

  const { data: csv, isLoading: isCSVDataLoading } = useFetch(
    ["export_downloads", exportReport?.id],
    `/api/exports/${exportReport?.id}/download/`,
    {},
    {
      enabled: !!exportReport,
      select: (res) => res.data,
    },
  );

  const data = useMemo(() => {
    if (!csv) return [];
    return csvParse(csv)?.map((row) => castNumbersInRowData(row));
  }, [csv]);

  return { data, report: reports?.[0], isLoading: isLoading || isCSVDataLoading };
};

const ProductExpander = ({ row }) => {
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const { product_id: productId, product } = row?.row?._original ?? {};

  return (
    <div style={{ backgroundColor: "#efefef", padding: "1rem" }}>
      <Card className="fs-standard mb-0">
        <Card.Body>
          <ProductCardHeader product={product} />
          <Row>
            <ProductShopperInsights product={product} productId={productId} marketPlace={marketPlace} showTabs />
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

const Dashboard = () => {
  const { t } = useTranslation(["merchandising"]);
  const hasAMCModule = useModuleEnabled("AMC");
  const [selectedCategory] = useQueryParams("category", null);
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const tableRef = useRef();
  const topHaloProductsTableRef = useRef();
  const TABLE_ID = "shopperinightsTable";
  const TOP_HALO_PRODUCTS_TABLE_ID = "shopperInsightsTop25ProductsTable";
  const filterProps = useCustomTableFilters(TABLE_ID);
  const topHaloProductsFilterProps = useCustomTableFilters(TOP_HALO_PRODUCTS_TABLE_ID);
  const [ordersToggle, setOrdersToggle] = useState(false);

  const { data: categories } = useCategoriesQuery(marketPlace.marketPlace, channel);
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const gatewayAsinsData = useLatestAMCData("GATEWAY_ASINS_INTERNAL");
  const gatewayAsinIndex = useMemo(() => {
    return gatewayAsinsData.data.reduce((acc, d) => {
      const rank = (d?.gateway_asin_rank && parseInt(d.gateway_asin_rank)) || null;
      acc[d.tracked_asin] = { ...d, gateway_asin_rank: rank };
      return acc;
    }, {});
  }, [gatewayAsinsData]);

  const categoryBreadcrumbs = useMemo(() => {
    return (
      categories?.map((d) => ({
        id: d.id,
        href: `/user/shopperinsightsV2?category=${d.id}`,
        name: `${d.leaf_category_name}`,
      })) ?? []
    );
  }, [categories]);

  const amcLTVData = useLatestAMCData("LTV_BY_AD_EXPOSURE_INTERNAL");

  const selectedCategoryBreadcrumb = categoryBreadcrumbs.find((d) => d.id.toString() === selectedCategory);

  const breadCrumbItems = [];
  if (channel === "amazon") {
    breadCrumbItems.push({
      name: t(($) => $.merchandising["AMC Dashboard"]),
      href: "/user/amc/dashboard",
      unsorted: true,
      dropdownLinkOptions: DASHBOARD_LINKS,
      activeOption: t(($) => $.merchandising["AMC Dashboard"]),
    });
    if (!breadCrumbItems[0].dropdownLinkOptions?.find((o) => o.name === AMC_QUERY_EXPLORER_BREADCRUMB_SPEC.name)) {
      breadCrumbItems[0].dropdownLinkOptions.push(AMC_QUERY_EXPLORER_BREADCRUMB_SPEC);
    }
  }
  breadCrumbItems.push({
    name: t(($) => $.merchandising["Shopper Insights"]),
    href: "/user/shopperinsightsV2",
  });

  if (selectedCategoryBreadcrumb) {
    breadCrumbItems.push({
      name: selectedCategoryBreadcrumb?.name,
      href: `/user/shopperinsightsV2?category=${selectedCategory}`,
      searchable: true,
      dropdownLinkOptions: categoryBreadcrumbs,
      searchPlaceholderText: t(($) => $.merchandising["Search Categories"]),
      dropdownSubtitle: t(($) => $.merchandising["Categories"]),
      activeOption: selectedCategoryBreadcrumb,
    });
  }

  const [filters, dimensions] = useMemo(() => {
    const filters = {
      marketplace: marketPlace.marketPlace,
      date__gte: start.format("YYYY-MM-DD"),
      date__lte: end.format("YYYY-MM-DD"),
    };
    if (selectedCategory) {
      filters.category_id = selectedCategory;
    }
    return [formatDatamartFilters(filters), group];
  }, [start, end, group, selectedCategory, marketPlace]);

  const { data: orgData, isLoading: orgDataLoading } = useFetch(
    ["shopperingsights", "org", marketPlace.marketPlace, dimensions, filters],
    "/api/data_report_v2/shopperinsightsorg/stats/",
    {
      dimensions,
      filters,
    },
    {
      enabled: !selectedCategory,
      select: (res) => res.data,
      default: [],
    },
  );

  const { data: orgDataTotal } = useFetch(
    ["shopperingsights", "org", marketPlace.marketPlace, "org_id", filters],
    "/api/data_report_v2/shopperinsightsorg/stats/",
    {
      dimensions: "org_id",
      filters,
    },
    {
      enabled: !selectedCategory,
      select: (res) => res.data,
      default: [],
    },
  );

  const { data: categoryData, isLoading: categoryDataLoading } = useFetch(
    ["shopperingsights", "category", selectedCategory, marketPlace.marketPlace, dimensions, filters],
    "/api/data_report_v2/shopperinsightscategory/stats/",
    {
      dimensions,
      filters,
    },
    {
      enabled: !!selectedCategory,
      select: (res) => res.data,
      default: [],
    },
  );

  const { data: categoryDataTotal } = useFetch(
    ["shopperingsights", "category", selectedCategory, marketPlace.marketPlace, "category_id", filters],
    "/api/data_report_v2/shopperinsightscategory/stats/",
    {
      dimensions: "category_id",
      filters,
    },
    {
      enabled: !!selectedCategory,
      select: (res) => res.data,
      default: [],
    },
  );

  const { data: categoryTableData } = useFetch(
    ["shopperingsights", "category", marketPlace.marketPlace, "category_id", filters],
    "/api/data_report_v2/shopperinsightscategory/stats/",
    {
      dimensions: "category_id",
      filters,
    },
    {
      enabled: !selectedCategory,
      select: (res) => res.data,
      default: [],
    },
  );
  const { productData: products, isLoading: isProductsDataLoading } = useProducts(
    "shopperInsights",
    selectedCategory,
    null,
    null,
  );

  const { data: productTableData } = useFetch(
    ["shopperingsights", "product", selectedCategory, marketPlace.marketPlace, "product_id", filters],
    "/api/data_report_v2/shopperinsights/stats/",
    {
      dimensions: "product_id",
      filters,
    },
    {
      select: (res) => res.data,
      default: [],
    },
  );

  const { ranks: productRanks } = useHaloRank(marketPlace);

  const [data, isLoading] = useMemo(() => {
    const data = selectedCategory ? categoryData : orgData;
    const total = selectedCategory ? categoryDataTotal : orgDataTotal;
    const isLoading = selectedCategory ? categoryDataLoading : orgDataLoading;
    return [
      {
        total: total?.[0] ?? {},
        date:
          data
            ?.map((d) => ({
              ...d,
              start_date: d.date,
              name: d.date,
            }))
            ?.sort((d) => d.start_date) ?? [],
      },
      isLoading,
    ];
  }, [selectedCategory, orgData, categoryData, orgDataLoading, categoryDataLoading, orgDataTotal, categoryDataTotal]);

  const { tableData } = useMemo(() => {
    if (selectedCategory) {
      const productMap =
        products?.reduce((acc, d) => {
          acc[d.id.toString()] = d;
          return acc;
        }, {}) ?? {};
      const tableData =
        productTableData?.map((d) => {
          const product = productMap[d.product_id];
          return {
            ...d,
            product,
            halo_rank: productRanks?.[d.product_id] ?? null,
            gateway_rank: product?.asin && (gatewayAsinIndex[product.asin]?.gateway_asin_rank ?? null),
          };
        }) ?? [];
      return { tableData };
    }
    const categoriesMap =
      categories?.reduce((acc, d) => {
        acc[d.id.toString()] = d;
        return acc;
      }, {}) ?? {};
    const tableData =
      categoryTableData?.map((d) => {
        return {
          ...d,
          category_name: categoriesMap[d.category_id]?.leaf_category_name,
        };
      }) ?? [];
    return { tableData };
  }, [selectedCategory, productTableData, categoryTableData, categories, products, productRanks, gatewayAsinIndex]);

  const top25ProductsTableData = useMemo(() => {
    const productMap =
      products?.reduce((acc, d) => {
        acc[d.id.toString()] = d;
        return acc;
      }, {}) ?? {};
    const top25ProductsData =
      productTableData
        ?.map((d) => {
          const product = productMap[d.product_id];
          return {
            ...d,
            product,
            halo_rank: productRanks?.[d.product_id] ?? null,
            gateway_rank: product?.asin && (gatewayAsinIndex[product.asin]?.gateway_asin_rank ?? null),
          };
        })
        ?.filter((product) => product.halo_rank)
        .sort((a, b) => a.halo_rank - b.halo_rank)
        ?.slice(0, 25) ?? [];
    return top25ProductsData;
  }, [products, productTableData, productRanks, gatewayAsinIndex]);

  const metrics = useMemo(() => {
    return [
      getMetricSpec("sales_ntb"),
      getMetricSpec("sales_repeat"),
      getMetricSpec("orders_ntb"),
      getMetricSpec("orders_repeat"),
      getMetricSpec("customer_value"),
      getMetricSpec("cac"),
      getMetricSpec("ltv_cac"),
      getMetricSpec("ad_spend"),
    ];
  }, []);

  const columnSpecs = useMemo(() => {
    const columns = [
      {
        key: "sales_ntb",
      },
      {
        key: "sales_repeat",
      },
      {
        key: "orders_ntb",
      },
      {
        key: "orders_repeat",
      },
      {
        key: "customer_value",
      },
    ];
    if (selectedCategory) {
      columns.push({
        key: "brand_customer_value",
      });
      columns.push({
        key: "purchase_freq",
      });
      columns.push({
        key: "halo_sales",
      });
      columns.push({
        key: "halo_rank",
      });
      if (hasAMCModule) {
        columns.push({
          key: "gateway_rank",
        });
      }
    } else {
      columns.push({
        key: "cac",
      });
      columns.push({
        key: "ltv_cac",
      });
      columns.push({
        key: "ad_spend",
      });
    }
    return columns;
  }, [selectedCategory, hasAMCModule]);

  const top25ProductsTableColumnSpecs = useMemo(() => {
    const cols = [
      {
        key: "halo_rank",
      },
      {
        key: "halo_sales",
      },
      {
        key: "sales_ntb",
      },
      {
        key: "sales_repeat",
      },
      {
        key: "orders_ntb",
      },
      {
        key: "orders_repeat",
      },
      {
        key: "customer_value",
      },
      {
        key: "brand_customer_value",
      },
      {
        key: "purchase_freq",
      },
    ];
    if (hasAMCModule) {
      cols.push({
        key: "gateway_rank",
      });
    }
    return cols;
  }, [hasAMCModule]);

  const barChartData = useMemo(() => {
    const orders = [
      { value: data.total.orders_ntb || 0, label: t(($) => $.merchandising["1st Order"]), sort: 5 },
      { value: data.total.orders_repeat_1 || 0, label: t(($) => $.merchandising["2nd Order"]), sort: 4 },
      { value: data.total.orders_repeat_2 || 0, label: t(($) => $.merchandising["3rd Order"]), sort: 3 },
      { value: data.total.orders_repeat_3 || 0, label: t(($) => $.merchandising["4th Order"]), sort: 2 },
      { value: data.total.orders_repeat_4 || 0, label: t(($) => $.merchandising["5th Orders"]), sort: 1 },
      { value: data.total.orders_repeat_5_or_more || 0, label: t(($) => $.merchandising["6+ Orders"]), sort: 0 },
    ];
    const sales = [
      { value: data.total.sales_ntb || 0, label: t(($) => $.merchandising["1st Sale"]), sort: 5 },
      { value: data.total.sales_repeat_1 || 0, label: t(($) => $.merchandising["2nd Sale"]), sort: 4 },
      { value: data.total.sales_repeat_2 || 0, label: t(($) => $.merchandising["3rd Sale"]), sort: 3 },
      { value: data.total.sales_repeat_3 || 0, label: t(($) => $.merchandising["4th Sale"]), sort: 2 },
      { value: data.total.sales_repeat_4 || 0, label: t(($) => $.merchandising["5th Sale"]), sort: 1 },
      { value: data.total.sales_repeat_5_or_more || 0, label: t(($) => $.merchandising["6+ Sales"]), sort: 0 },
    ];
    return {
      orders,
      sales,
    };
  }, [data, t]);

  const ltvByAdExposure = useMemo(() => {
    if (!amcLTVData.data) return [];
    let exposed = amcLTVData.data.find((d) => d.exposure === "exposed")?.ltv || "0";
    exposed = parseFloat(exposed);
    let unexposed = amcLTVData.data.find((d) => d.exposure === "unexposed")?.ltv || "0";
    unexposed = parseFloat(unexposed);
    const total = data.total.customer_value || 0;
    return [
      { value: total, label: t(($) => $.merchandising["Overall"]), sort: 3 },
      { value: exposed, label: t(($) => $.merchandising["Exposed"]), sort: 2 },
      { value: unexposed, label: t(($) => $.merchandising["Unexposed"]), sort: 1 },
    ];
  }, [amcLTVData, data, t]);

  const numberFormatter = (v) => formatNumber(v, { decimalPlaces: 0 });
  const currencyFormatter = (v) =>
    formatCurrency(v, {
      marketPlace: marketPlace.marketPlace,
    });

  return (
    <div style={{ minHeight: "calc(100vh - 150px" }}>
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row className="pb-5">
        <Col xs={12} className="px-5">
          <MetricsCard
            id="shopperInsightsV2Dashboard"
            title={
              <>
                <span>
                  {selectedCategory ?
                    t(($) => $.merchandising["Shopper Insights {category}"], {
                      category: selectedCategoryBreadcrumb?.name || "",
                    })
                  : t(($) => $.merchandising["Shopper Insights Overview"])}
                </span>
                {hasAMCModule && (
                  <small className="d-block fs-4 pt-1 pb-2" style={{ fontWeight: 400 }}>
                    {t(($) => $.merchandising["Powered by AMC"])}
                  </small>
                )}
              </>
            }
            initialSelectedMetric={["sales_ntb", "sales_repeat", "orders_ntb", "orders_repeat", "customer_value"]}
            totals={{
              data: data?.total,
              loading: isLoading,
            }}
            timeline={{
              data: data?.date || [],
              loading: isLoading,
            }}
            metrics={metrics}
            group={group}
            showMetricsSelector={true}
            context={"dspCampaign"}
            byline={
              <>
                {longDate.format(start.toDate())}
                {" - "}
                {longDate.format(end.toDate())}
              </>
            }
          >
            {!selectedCategory && (
              <Row>
                <Col xs={6}>
                  <ButtonGroup
                    buttons={[
                      { label: t(($) => $.merchandising["Sales"]), value: "sales" },
                      {
                        label: t(($) => $.merchandising["Orders"]),
                        value: "orders",
                      },
                    ]}
                    value={ordersToggle === true ? "orders" : "sales"}
                    onSelect={(value) => setOrdersToggle(value === "orders")}
                    colorPalette={"buttonGroup.fuchsia"}
                  />
                </Col>
              </Row>
            )}
            <Row>
              {(!hasAMCModule || selectedCategory || ordersToggle) && (
                <Col xs={6}>
                  <BarChart
                    formatter={numberFormatter}
                    type="column"
                    data={barChartData.orders}
                    accessor={(d) => d.value}
                    categoryAccessor={(d) => d.label}
                    metric={t(($) => $.merchandising["Orders"])}
                    title={t(($) => $.merchandising["Orders Breakdown"])}
                    sortAccessor={(d) => d.sort}
                    topN={10}
                    height={300}
                    colors={[
                      metricColors.ntb[0],
                      metricColors.repeat[4],
                      metricColors.repeat[3],
                      metricColors.repeat[2],
                      metricColors.repeat[1],
                      metricColors.repeat[0],
                    ]}
                    colorByCategory={true}
                  />
                </Col>
              )}
              {(!hasAMCModule || selectedCategory || !ordersToggle) && (
                <Col xs={6}>
                  <BarChart
                    formatter={currencyFormatter}
                    type="column"
                    data={barChartData.sales}
                    accessor={(d) => d.value}
                    categoryAccessor={(d) => d.label}
                    sortAccessor={(d) => d.sort}
                    topN={10}
                    height={300}
                    metric={t(($) => $.merchandising["Sales"])}
                    title={t(($) => $.merchandising["Sales Breakdown"])}
                    colors={[
                      metricColors.ntb[0],
                      metricColors.repeat[4],
                      metricColors.repeat[3],
                      metricColors.repeat[2],
                      metricColors.repeat[1],
                      metricColors.repeat[0],
                    ]}
                    colorByCategory={true}
                  />
                </Col>
              )}
              {hasAMCModule && !selectedCategory && (
                <Col xs={6}>
                  <BarChart
                    type="column"
                    formatter={currencyFormatter}
                    data={ltvByAdExposure}
                    accessor={(d) => d.value}
                    categoryAccessor={(d) => d.label}
                    sortAccessor={(d) => d.order}
                    metric={t(($) => $.merchandising["LTV"])}
                    title={t(($) => $.merchandising["LTV by Ad Exposure"])}
                    height={300}
                    colors={[metricColors.sales[4]]}
                  />
                </Col>
              )}
            </Row>
            {hasAMCModule && !selectedCategory && (
              <Row className="mb-5">
                <Col xs={6}></Col>
                <Col xs={6} style={{ textAlign: "right" }}>
                  <small>{t(($) => $.merchandising["Powered by Amazon Marketing Cloud"])}</small>
                </Col>
              </Row>
            )}
            <Accordion
              title={selectedCategory ? t(($) => $.merchandising["Products"]) : t(($) => $.merchandising["Categories"])}
              defaultExpanded={true}
            >
              <Table
                marketPlace={marketPlace}
                tableRef={tableRef}
                tableId={TABLE_ID}
                isExpandable={!!selectedCategory}
                isSelectable={false}
                titleCol={{
                  id: selectedCategory ? "asin" : "category_name",
                  isStatic: true,
                  Header: selectedCategory ? t(($) => $.merchandising["ASIN"]) : t(($) => $.merchandising["Category"]),
                  accessor: "text",
                  format: "text",
                  style: { width: "300px" },
                  width: 450,
                  Cell: ({ row }) => {
                    const {
                      category_id: categoryId,
                      category_name: categoryName,
                      product_id: productId,
                      product,
                    } = row?._original ?? {};

                    if (product) {
                      return (
                        <ProductCell
                          original={{
                            ...product,
                            id: product.product_id,
                            cellContext: "shopperInsights",
                          }}
                          value={product.product_title}
                          row={row}
                          showAsin={true}
                          showInventoryBadge={true}
                        />
                      );
                    }
                    const image =
                      categoryId ?
                        `${URL_PREFIX}/api/productimage/?category=${categoryId}`
                      : `${URL_PREFIX}/api/productimage/?product=${productId}`;
                    const to =
                      categoryId ?
                        `/user/shopperinsightsV2?category=${categoryId}`
                      : `/user/merchandising/product/${productId}`;
                    return (
                      <TrellisLink
                        to={to}
                        style={{
                          textDecoration: "none",
                          fontWeight: "500",
                          fontSize: "1.5rem",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex" }}>
                          <div
                            style={{
                              userSelect: "none",
                              WebkitUserSelect: "none",
                              flexShrink: 0,
                              width: 50,
                              height: 50,
                              marginRight: "10px",
                              background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
                            }}
                            draggable="false"
                          />
                          <div>{categoryName}</div>
                        </div>
                      </TrellisLink>
                    );
                  },
                  Filter: () => null,
                }}
                data={tableData}
                columnSpecs={columnSpecs}
                excludedFilterColumns={["conversion_type"]}
                getSearchCriteria={(row) => {
                  if (selectedCategory) {
                    const { asin, sku, product_title } = row.product ?? {};
                    return `${asin} ${sku} ${product_title}`;
                  }
                  const { category_name } = row ?? {};
                  return `${category_name}`;
                }}
                tableRowDescription={t(($) => $.merchandising["Products"])}
                defaultSorted={[{ id: "dsp_total_detail_page_views", desc: true }]}
                isLoading={isLoading}
                filterWidget={true}
                hideFilterToggleIcon={true}
                hideResetFiltersIcon={false}
                {...filterProps}
                handleBulkChanges={() => {}}
                minRows={1}
                ExpandedContent={ProductExpander}
                selectionKey="product_id"
              />
            </Accordion>
            {!selectedCategory && top25ProductsTableData?.length ?
              <Accordion title={t(($) => $.merchandising["Top 25 Halo Products"])} defaultExpanded={false}>
                <Table
                  marketPlace={marketPlace}
                  tableRef={topHaloProductsTableRef}
                  tableId={TOP_HALO_PRODUCTS_TABLE_ID}
                  isExpandable={true}
                  isSelectable={false}
                  isLoading={isProductsDataLoading}
                  titleCol={{
                    id: "asin",
                    isStatic: true,
                    Header: t(($) => $.merchandising["ASIN"]),
                    accessor: "text",
                    format: "text",
                    style: { width: "300px" },
                    width: 450,
                    Cell: ({ row }) => {
                      const { product } = row?._original ?? {};
                      if (!product) return null;

                      return (
                        <ProductCell
                          original={{
                            ...product,
                            id: product.product_id,
                            cellContext: "shopperInsights",
                          }}
                          value={product.product_title}
                          row={row}
                          showAsin={true}
                          showInventoryBadge={true}
                        />
                      );
                    },
                    Filter: () => null,
                  }}
                  data={top25ProductsTableData}
                  columnSpecs={top25ProductsTableColumnSpecs}
                  excludedFilterColumns={["conversion_type"]}
                  getSearchCriteria={(row) => {
                    const { asin, sku, product_title } = row.product ?? {};
                    return `${asin} ${sku} ${product_title}`;
                  }}
                  tableRowDescription={t(($) => $.merchandising["Products"])}
                  defaultSorted={[{ id: "halo_rank", desc: false }]}
                  filterWidget={true}
                  hideFilterToggleIcon={true}
                  hideResetFiltersIcon={false}
                  {...topHaloProductsFilterProps}
                  handleBulkChanges={() => {}}
                  minRows={1}
                  ExpandedContent={ProductExpander}
                  selectionKey="product_id"
                />
              </Accordion>
            : null}
          </MetricsCard>
        </Col>
      </Row>
    </div>
  );
};

const ShopperInsightsV2 = () => {
  return (
    <DatesProvider>
      <Dashboard />
    </DatesProvider>
  );
};

export default ShopperInsightsV2;
